import { Avatar, Box, Card, CardActionArea, CardContent, Chip, Stack, Typography } from "@mui/material";

import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";
import { makeProfileAvatarUrl } from "~utils/url";

import {
  formatLocalDateTime,
  participantCardBackgroundSx,
  participantDisplayName,
  participantInitials,
  participantRosterCardCenteredRowSx,
  participantRosterCardContainSx,
  participantRosterCardContentSx,
  participantRosterCardDetailRowSx,
  participantRosterCardStackSx,
  participantRosterCardTextColumnSx,
  type ParticipantRow,
  statusLabelForKey,
} from "../../pages/VisitorManagement/emergencyCommandCenterShared";

export interface ParticipantRosterCardProps {
  participant: ParticipantRow;
  onSelect: (participant: ParticipantRow) => void;
  /** When set and equal to `participant.userId`, shows a "You" chip next to the name. */
  currentUserId?: string | null;
  /** Smaller avatar, padding, and type — use on command-center rosters to show more people per screen. */
  compact?: boolean;
}

export function ParticipantRosterCard({
  participant: p,
  onSelect,
  currentUserId,
  compact = false,
}: ParticipantRosterCardProps) {
  const roles = (p.bubbleRoles ?? []).filter(Boolean);
  const showYouChip = Boolean(currentUserId && p.userId && currentUserId === p.userId);
  const avatarPx = compact ? 52 : 96;
  const contentSx = compact
    ? {
        ...participantRosterCardContentSx,
        "&:last-child": { pb: 1.25 },
        p: 1.25,
      }
    : participantRosterCardContentSx;
  const nameVariant = compact ? "subtitle2" : "subtitle1";
  const detailVariant = compact ? "caption" : "body2";
  const nameSx = compact
    ? { ...participantRosterCardCenteredRowSx, fontWeight: 600 }
    : participantRosterCardCenteredRowSx;

  return (
    <Card
      sx={{
        ...participantCardBackgroundSx(p.statusKey),
        ...participantRosterCardContainSx,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      variant="outlined"
    >
      <CardActionArea
        aria-label={`Update status for ${participantDisplayName(p)}`}
        component="div"
        onClick={() => {
          onSelect(p);
        }}
        sx={{
          alignItems: "stretch",
          borderRadius: 1,
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
          width: "100%",
        }}
      >
        <CardContent sx={contentSx}>
          <Stack alignItems="center" direction="column" gap={compact ? 1 : 1.5} sx={participantRosterCardStackSx}>
            <Avatar
              alt=""
              src={p.userId ? makeProfileAvatarUrl(p.userId) : undefined}
              sx={{ flexShrink: 0, height: avatarPx, width: avatarPx }}
            >
              {participantInitials(p)}
            </Avatar>
            <Stack spacing={compact ? 0.5 : 1} sx={participantRosterCardTextColumnSx}>
              {showYouChip ? (
                <Stack
                  alignItems="center"
                  direction="row"
                  flexWrap="wrap"
                  justifyContent="center"
                  sx={{ gap: compact ? 0.75 : 1 }}
                >
                  <Typography component="span" sx={nameSx} variant={nameVariant}>
                    {participantDisplayName(p)}
                  </Typography>
                  <Chip label="You" size="small" sx={{ flexShrink: 0, height: compact ? 22 : undefined }} />
                </Stack>
              ) : (
                <Typography sx={nameSx} variant={nameVariant}>
                  {participantDisplayName(p)}
                </Typography>
              )}
              {p.phoneNumber?.trim() ? (
                <Box
                  sx={
                    compact
                      ? {
                          "& .MuiIconButton-root": { padding: "2px" },
                          "& .MuiTypography-root": { fontSize: "0.75rem", lineHeight: 1.35 },
                        }
                      : undefined
                  }
                >
                  <PhoneWithCopyIcon centered phoneNumber={p.phoneNumber.trim()} showPhoneIcon={false} />
                </Box>
              ) : (
                <Typography color="text.secondary" sx={participantRosterCardCenteredRowSx} variant={detailVariant}>
                  —
                </Typography>
              )}
              <Typography color="text.secondary" sx={participantRosterCardDetailRowSx} variant={detailVariant}>
                <strong>Role:</strong> {roles.length > 0 ? roles.join(", ") : "—"}
              </Typography>
              <Typography sx={participantRosterCardDetailRowSx} variant={detailVariant}>
                <strong>Status:</strong> {statusLabelForKey(p.statusKey)}
              </Typography>
              <Typography color="text.secondary" sx={participantRosterCardDetailRowSx} variant={detailVariant}>
                <strong>Last update:</strong> {formatLocalDateTime(p.lastReportedAtUtc)}
              </Typography>
              {p.mapLocationLabel ? (
                <Typography sx={participantRosterCardDetailRowSx} variant={detailVariant}>
                  <strong>Location:</strong> {p.mapLocationLabel}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
